/**
 * The scorer's arithmetic, and the projection of a queue onto server truth.
 *
 * A DELIBERATE MIRROR of calculateRemainingScore in functions/lib/scoring.js.
 * Nothing in src/ imports from functions/ — the precedent is PLAYING_STATUSES
 * in StartTournamentModal.jsx — and a scorer that cannot compute a remaining
 * score without asking the server is a scorer that stops working the moment
 * the wifi does. If the bust rule changes, it changes in both places.
 *
 * The server stays authoritative. It can force a bust this never declared
 * (501 leaves no opinion about finishing below zero or on one) and its answer
 * replaces this one on the next sync. What lives here only has to be right
 * enough to drive the screen between now and then.
 */

export const STARTING_SCORE = 501;

/**
 * One player's running total through a leg.
 *
 * A declared bust stays a bust; an undeclared one becomes a bust when the
 * numbers demand it. A bust scores nothing and leaves the total where it was,
 * while still having cost its darts.
 */
export function remainingAfter(visits, playerId) {
  let remaining = STARTING_SCORE;

  for (const visit of visits) {
    if (visit.player_id !== playerId) continue;

    const thrown = visit.is_bust === 1 ? 0 : (visit.score ?? 0);
    const after  = remaining - thrown;

    if (!(visit.is_bust === 1 || after < 0 || after === 1)) remaining = after;
  }

  return remaining;
}

/** What a score of `score` would do to `remaining`, without applying it. */
export function outcomeOf(remaining, score) {
  const after = remaining - score;

  if (after === 0)            return 'checkout';
  if (after < 0 || after === 1) return 'bust';
  return 'ok';
}

/**
 * Whether the projected match is over, by legs won.
 *
 * Mirrors the room: first to floor(bestOf / 2) + 1.
 */
export function legsNeeded(bestOfLegs) {
  return Math.floor((bestOfLegs ?? 3) / 2) + 1;
}

/* ------------------------------------------------------------------------- */

/**
 * Server state with the unsent queue replayed on top.
 *
 * WHY PROJECT RATHER THAN MUTATE. The room replaces the board wholesale on
 * every sync, so a locally-mutated game state would have to be merged with it
 * — and a merge that disagrees with the server is exactly the drift V1 could
 * never reconstruct afterwards. Here the board is swapped in, this runs again,
 * and the screen is correct by construction. Nothing in storage is ever read
 * as game state; the queue holds events still to send, never a picture of the
 * match.
 *
 * Pure, and derived only from (match, queue) — the same two inputs always give
 * the same screen.
 */
export function projectMatch(match, queue) {
  if (!match) return null;

  const visits = queue.filter(
    (item) => item.type === 'submit_visit' && item.payload.matchId === match.id
  );

  // Cloned to the depth this touches. The caller's copy is the room's and is
  // replaced by the next sync; writing into it would corrupt the base state
  // that the next projection starts from.
  const legs = (match.legs ?? []).map((leg) => ({
    ...leg,
    visits: [...(leg.visits ?? [])],
  }));

  const p1 = match.player1_id;
  const p2 = match.player2_id;

  for (const item of visits) {
    const { legNumber, playerId, score, dartsUsed, isBust } = item.payload;

    let leg = legs.find((l) => l.leg_number === legNumber);

    // A leg opened offline has no row yet. Same alternation the room applies
    // when it opens one itself — and the room's answer wins on the next sync,
    // so a wrong guess here costs one refresh, not a wrong result.
    if (!leg) {
      const previous = legs[legs.length - 1] ?? null;

      leg = {
        id:                 null,
        leg_number:         legNumber,
        starting_player_id: previous
          ? (previous.starting_player_id === p1 ? p2 : p1)
          : p1,
        winner_id: null,
        visits:    [],
        pending:   true,
      };
      legs.push(leg);
    }

    const before = remainingAfter(leg.visits, playerId);
    const after  = before - score;
    const busted = isBust === true || after < 0 || after === 1;

    leg.visits.push({
      id:              null,
      client_id:       item.clientId,
      leg_id:          leg.id,
      player_id:       playerId,
      visit_number:    leg.visits.filter((v) => v.player_id === playerId).length + 1,
      score:           busted ? 0 : score,
      darts_used:      dartsUsed,
      remaining_score: busted ? before : after,
      is_bust:         busted ? 1 : 0,
      pending:         true,
    });

    if (!busted && after === 0) leg.winner_id = playerId;
  }

  for (const leg of legs) {
    leg.remaining = {
      [p1]: remainingAfter(leg.visits, p1),
      [p2]: remainingAfter(leg.visits, p2),
    };
  }

  const legsWon = { [p1]: 0, [p2]: 0 };
  for (const leg of legs) {
    if (leg.winner_id != null) legsWon[leg.winner_id] = (legsWon[leg.winner_id] ?? 0) + 1;
  }

  const needed  = legsNeeded(match.best_of_legs);
  const decided = legsWon[p1] >= needed || legsWon[p2] >= needed;

  let current = legs.find((leg) => leg.winner_id == null) ?? null;

  // Every leg is won and the match is not: the next one is open in the room
  // and nobody has thrown in it yet. Synthesised HERE rather than in the page
  // so the screen after a checkout is the same code path as the screen before
  // it — the marker throws into leg N+1 and its first visit creates it for
  // real. Offline this is the only thing standing between a checkout and the
  // players carrying on.
  if (!current && !decided) {
    const previous = legs[legs.length - 1] ?? null;

    current = {
      id:                 null,
      leg_number:         (previous?.leg_number ?? 0) + 1,
      starting_player_id: previous
        ? (previous.starting_player_id === p1 ? p2 : p1)
        : p1,
      winner_id: null,
      visits:    [],
      remaining: { [p1]: STARTING_SCORE, [p2]: STARTING_SCORE },
      pending:   true,
    };
    legs.push(current);
  }

  return {
    ...match,
    legs,
    legsWon,
    legsToWin:       needed,
    currentLegId:    current?.id ?? null,
    currentLeg:      current,
    decided,
    projectedWinner: decided ? (legsWon[p1] >= needed ? p1 : p2) : null,
  };
}
